import React, { useEffect, useState, lazy, Suspense } from "react";
import {
  userRoles,
  productPlans,
  procedureCodes,
  roleAccess,
  productPlanAccess,
} from "./Roles";
import {
  Button,
  Container,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { xorDecrypt } from "../../../encrypt";

const MainPage = () => {
  const [userRole, setUserRole] = useState(null);
  const [userProductPlan, setUserProductPlan] = useState(null);
  const [selectedProcedureCode, setSelectedProcedureCode] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFormFilled, setIsFormFilled] = useState(false);

  useEffect(() => {
    try {
      const storedRole = xorDecrypt(localStorage.getItem("userRole"));
      //console.log("storedRole:", storedRole); // Debug log

      if (storedRole === "admin") {
        setUserRole(storedRole);
      } else {
        const storedProductPlan = xorDecrypt(
          localStorage.getItem("productPlan")
        );
        //console.log("storedProductPlan:", storedProductPlan); // Debug log
        if (storedRole && storedProductPlan) {
          setUserRole(storedRole);
          setUserProductPlan(storedProductPlan);
        }
      }
    } catch (error) {
      console.error("Error accessing localStorage", error);
    }
  }, []);

  if (!userRole || (userRole !== "admin" && !userProductPlan)) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const accessibleProcedureCodes = [
    ...(roleAccess[userRole] || []),
    ...(userRole !== "admin" ? productPlanAccess[userProductPlan] || [] : []),
  ];

  const handleProcedureCodeClick = (code) => {
    setSelectedProcedureCode(code);
  };

  const handleFormChange = (isFilled) => {
    setIsFormFilled(isFilled);
  };


  const handleBackButtonClick = () => {
    if (isFormFilled) {
      setIsDialogOpen(true);
    } else {
      setSelectedProcedureCode(null);
    }
  };

  const handleDialogClose = (confirm) => {
    setIsDialogOpen(false);
    if (confirm) {
      setSelectedProcedureCode(null);
    }
  };

  
  const colorfulTextStyle = {
    background: "linear-gradient(45deg, #ffa500, #ffffff, #ffa500)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    fontWeight: "bold",
    marginBottom: "30px",
    marginTop: "20px",
  };

  const procedureCodeComponents = procedureCodes.reduce((acc, procedure) => {
    acc[procedure.code] = procedure.component;
    return acc;
  }, {});

  const SelectedProcedureComponent = selectedProcedureCode
    ? procedureCodeComponents[selectedProcedureCode]
    : null;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        backgroundColor: "none",
      }}
    >
      <Container
        sx={{ margin: 1, padding: 2, borderRadius: 1, backgroundColor: "none" }}
      >
        {!selectedProcedureCode ? (
          <>
            <Typography variant="h4" gutterBottom sx={colorfulTextStyle}>
              Select Procedure Code:
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {accessibleProcedureCodes.map((procedure) => (
                <Grid item key={procedure.code}>
                  <Button
                    variant="contained"
                    sx={{
                      backgroundColor: "orange",
                      color: "white",
                      padding: "10px 20px",
                      margin: "5px",
                      fontSize: "16px",
                      "&:hover": { backgroundColor: "darkorange" },
                    }}
                    onClick={() => handleProcedureCodeClick(procedure.code)}
                  >
                    {procedure.name}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </>
        ) : (
          <Box
            sx={{
              maxHeight: "80vh", // Adjust based on your layout needs
              overflowX: "hidden", // Hide horizontal scrollbar
              overflowY: "auto", // Enable vertical scrollbar if content overflows
              // Custom scrollbar styles
              "&::-webkit-scrollbar": {
                width: "4px", // Width of the scrollbar
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#e9a132", // Scrollbar thumb color (orange)
                borderRadius: "8px", // Rounded corners for the scrollbar thumb
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#d0892f", // Darken the thumb on hover
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#333", // Scrollbar track color
                borderRadius: "8px", // Rounded corners for the scrollbar track
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Tooltip title="Go Back">
                <IconButton onClick={handleBackButtonClick} sx={{ color: "orange", top: "-5px", margin: "5px" }}>
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Typography variant="h5" gutterBottom sx={colorfulTextStyle}>
                Generate ABA Notes For Procedure Code: {selectedProcedureCode}
              </Typography>
            </Box>
            <Suspense fallback={<CircularProgress />}>
              {SelectedProcedureComponent && (
                <SelectedProcedureComponent onFormChange={handleFormChange} />
              )}
            </Suspense>
          </Box>
        )}
      </Container>
      <Dialog
  open={isDialogOpen}
  onClose={() => handleDialogClose(false)}
  PaperProps={{
    sx: {
      backgroundColor: "#333", // Change background color to dark grey
      color: "#fff",
      boxShadow: "0px 0px 15px 5px rgba(255, 255, 255, 0.2)",
      borderRadius: "10px",
    },
  }}
>
  <DialogTitle
    sx={{
      color: "#ffa500",
      fontWeight: "bold",
    }}
  >
    Confirm Navigation
  </DialogTitle>
  <DialogContent>
    <DialogContentText sx={{ color: "#fff" }}>
      Are you sure you want to go back? The data will be cleared.
    </DialogContentText>
  </DialogContent>
  <DialogActions>
    <Button
      onClick={() => handleDialogClose(false)}
      sx={{
        color: "#ffa500",
        "&:hover": { backgroundColor: "rgba(255, 165, 0, 0.1)" },
      }}
    >
      Cancel
    </Button>
    <Button
      onClick={() => handleDialogClose(true)}
      sx={{
        color: "#ffa500",
        "&:hover": { backgroundColor: "rgba(255, 165, 0, 0.1)" },
      }}
      autoFocus
    >
      Confirm
    </Button>
  </DialogActions>
</Dialog>
    </Box>
  );
};

export default MainPage;